<template>
  <el-dialog
    id="edit-modal"
    class="form-modal"
    v-loading="editProcessing"
    :visible.sync="editVisible"
    :fullscreen="true"
    :show-close="false"
    :modal="false"
  >
    <div id="edit-modal-header" class="modal-header">
      <div class="modal-header-layout flex grid-6 align-center">
        <h1 class="modal-title col-2">Edit / View Virtual Receptionist</h1>
        <div class="modal-actions col-2">
          <el-button type="primary" @click="editOperation">Save</el-button>
          <el-button
            type="default"
            class="hidden-xs-only"
            @click="cancelOperation"
          >
            Cancel
          </el-button>
          <el-button
            type="default"
            icon="el-icon-close"
            class="hidden-sm-and-up"
            @click="cancelOperation"
          />
        </div>
      </div>
    </div>

    <el-form>
      <recent-audits service="Virtual Receptionist" :record="selectedIvr" />
      <el-tabs v-model="activeTabName">
        <el-tab-pane label="General" name="general">
          <div class="inline-form-group">
            <el-checkbox v-model="ivSpeechDetect" :label="true">
              <span>Enable speech detection</span>
            </el-checkbox>
          </div>
          <el-tag class="mb-10">
            <i class="el-icon-info"></i>
            Allow callers to speak key options
          </el-tag>

          <el-input v-model="editForm.name" placeholder="Enter name">
            <template slot="prefix">Name</template>
          </el-input>

          <div class="inline-form-group">
            <el-checkbox v-model="extensionDialing" :label="true">
              <span>Enable extension dialing</span>
            </el-checkbox>
          </div>
          <dads-select
            v-model="editForm.branchId"
            placeholder="Branch"
            filterable
          >
            <template slot="prefix"> Branch</template>
            <el-option
              v-for="branch in branchOptions"
              :key="branch.id"
              :value="branch.id"
              :label="branch.name"
            />
          </dads-select>

          <!-- TIMEOUT DESTINATION -->
          <h2 class="form-section-header">On timeout</h2>
          <destination-selector
            :isTerminalDestSelector="true"
            :draggable="true"
            :showHeader="false"
            :draggableDestTypes="draggableDestTypes"
              :include-pharmacy-dests="true"
                :include-pharmacy-agents="true"
            :selectedDestinations="editForm.timeoutDestinations"
            @destinationsSelected="onTimeoutDestinationsSelection"
            title=""
            :clearFields="clearFormFields"
          />

          <div>
            <el-select
              v-model="editForm.timeout"
              placeholder="Select timeout (seconds)"
            >
              <el-option v-for="i in 20" :key="i" :label="i" :value="i" />
              <template slot="prefix">Select timeout (seconds)</template>
            </el-select>
          </div>

          <!-- WRONGKEYPRESS DESTINATION -->
          <h2 class="form-section-header">If wrong key pressed</h2>
          <destination-selector
            :isTerminalDestSelector="true"
            :draggable="true"
            :showHeader="false"
            :draggableDestTypes="draggableDestTypes"
              :include-pharmacy-dests="true"
                :include-pharmacy-agents="true"
            :selectedDestinations="editForm.wrongKeyPressDestinations"
            @destinationsSelected="onWrongKeyPressDestinationsSelection"
            title=""
            :clearFields="clearFormFields"
          />

          <!-- LOOPING SETTINGS -->
          <div class="mb-5">
            <h2 class="form-section-header">Loop settings</h2>
          </div>

          <div class="inline-form-group">
            <el-checkbox v-model="editForm.loopOnTimeout" :label="true">
              <span>Enable loop on timeout</span>
            </el-checkbox>
          </div>
          <div class="inline-form-group">
            <el-checkbox v-model="editForm.loopOnWrongKeyPress" :label="true">
              <span>Enable loop on wrong key</span>
            </el-checkbox>
          </div>

          <div v-if="editForm.loopOnTimeout || editForm.loopOnWrongKeyPress">
            <el-select
              v-model="editForm.maxLoops"
              placeholder="Select max loops"
            >
              <template slot="prefix">Max loops</template>
              <el-option v-for="i in 5" :key="i" :value="i" :label="i" />
            </el-select>
          </div>

          <!-- AVAILABLE NUMBERS -->
          <div v-if="editForm.phoneNums.length">
            <h2 class="form-section-header">Assign phone numbers</h2>
            <el-select
              v-model="editForm.phoneNums"
              multiple
              :disabled="true"
              placeholder=""
            >
              <template slot="prefix"
                ><span style="opacity: 0">Numbers</span></template
              >
              <el-option
                v-for="item in numberOptions"
                :key="item.key"
                :label="item.label | formatNumber"
                :value="item.key"
              />
            </el-select>
            <el-tag class="mb-10">
              <i class="el-icon-info" style="margin-right: 4px;" />
              You can modify this in the phone numbers section.
            </el-tag>
          </div>
        </el-tab-pane>

        <!-- ⭐ MEDIAFILES ⭐ -->
        <el-tab-pane label="Media" name="media">
          <existing-mediafile
            title="Greeting messages"
            :multiple="!ivSpeechDetect"
            :clearState="clearFormFields"
            :newMediafile="newMediafile"
            :selectedMeIds="editForm.meIds"
            :selectedMeId="selectedMeId"
            @mediafilesSelected="onMediafilesSelected"
            @mediafileSelected="onMediafileSelected"
          />
          <el-tag v-if="ivSpeechDetect" class="mb-10">
            <i class="el-icon-info"></i>
            only one media file is supported when speech detect is enabled
          </el-tag>
          <new-mediafile @mediafileSaved="onNewMediafileCreation" />
        </el-tab-pane>

        <!-- KEYPRESS OPTIONS -->
        <el-tab-pane
          label="Key Press Options"
          name="keypress"
          class="key-press-options"
        >
          <div
            style="margin-bottom: 8px;"
            v-for="(key, i) in keyOptions"
            :key="i"
          >
            <h2 class="form-section-header">
              Key {{ getKeyLabel(key) }} Destinations
            </h2>
            <destination-selector
              :isTerminalDestSelector="true"
              :draggable="true"
              :showHeader="false"
              :draggableDestTypes="draggableDestTypes"
              :include-pharmacy-dests="true"
                :include-pharmacy-agents="true"
              :selectedDestinations="editForm[`key${key}Destinations`]"
              @destinationsSelected="onKeyDestinationsSelection(key, $event)"
              title=""
              :clearFields="clearFormFields"
            />
          </div>
        </el-tab-pane>

        <el-tab-pane
          label="Announcements"
          name="announcements"
        >
          <draggable
            :key="draggableKey"
            @change="changeDraggableKey"
            v-model="announcementKeys"
          >
            <transition-group name="list" tag="div">
              <div
                class="inline-form-group"
                v-for="(item, i) in announcementKeys"
                :key="i + '$(^^)$'"
              >
                <el-col :span="18">
                  <dads-select
                    :placeholder="`Select Announcement ${i + 1}`"
                    :remote-method="(query) => remoteSearchAnnouncements(query, i)"
                    v-model="item.id"
                    remote
                    filterable
                    clearable
                    @change="($event) => onAnnouncementSelection($event, i)"
                  >
                    <el-option
                      v-for="(el, j) in item.options"
                      :key="j"
                      :label="el.name"
                      :value="el.id"
                    />
                  </dads-select>
                </el-col>
                <el-col :span="1" />
                <el-col :span="5" class="text-right">
                  <el-tooltip
                    class="item"
                    effect="dark"
                    :content="`Remove Announcement ${i + 1}`"
                    placement="top"
                  >
                    <el-button
                      class="ml-5"
                      style="float: right; margin-right: 1px"
                      @click="removeAnnouncementKey(i)"
                      icon="el-icon-delete"
                    />
                  </el-tooltip>
                  <el-button icon="el-icon-rank" type="default" />
                </el-col>
              </div>
            </transition-group>
          </draggable>
          <el-button
            type="text"
            icon="el-icon-circle-plus-outline"
            @click="addAnnouncementKey"
          >
            Add Announcement
          </el-button>
        </el-tab-pane>
      </el-tabs>
    </el-form>

    <destination-references
      :tenantId="selectedIvr ? selectedIvr.tenantId : null"
      type="IVR"
      :id="selectedIvr ? selectedIvr.id : null"
      :clearState="clearFormFields"
    />
  </el-dialog>
</template>

<script>
import Draggable from "vuedraggable";
import { isEqual } from "lodash";
import DestinationSelector from "@/components/DestinationSelector.vue";
import ExistingMediafile from "@/components/ExistingMediafile";
import NewMediafile from "@/components/NewMediafile";
import DestinationReferences from "@/components/DestinationReferences";
import RecentAudits from "@/components/RecentAudits";
import DadsSelect from "@/components/select";
import { apiV2Host } from "@/feathers/feathers-client";

export default {
  name: "editIvrForm",

  props: {
    editVisible: { required: true },
    editProcessing: { required: true },
    numberOptions: { required: true },
    clearFormFields: { required: true },
    selectedIvr: { required: true },
    reRenderKey: { required: true },
    draggableDestTypes: { required: true },
    branchOptions: { required: true }
  },

  components: {
    Draggable,
    DadsSelect,
    NewMediafile,
    ExistingMediafile,
    DestinationSelector,
    DestinationReferences,
    RecentAudits
  },

  data() {
    return {
      newMediafile: null,
      activeMediafileTabName: "existing",
      keyOptions: [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, "SHARP", "STAR"],
      greetingType: "",
      existingGreetingMsgName: "",
      activeTabName: "general",
      ivSpeechDetect: false,
      extensionDialing: false,
      selectedMeId: null,
      announcementOptions: null,
      draggableKey: "",
      announcementKeys: [],
      editForm: {
        name: "",
        phoneNums: [],
        phoneNumsChanged: false,
        timeout: null,
        timeoutDestinations: [],
        wrongKeyPressDestinations: [],
        loopOnTimeout: false,
        loopOnWrongKeyPress: false,
        maxLoops: null,
        allowDisa: false,
        key0Destinations: [],
        key1Destinations: [],
        key2Destinations: [],
        key3Destinations: [],
        key4Destinations: [],
        key5Destinations: [],
        key6Destinations: [],
        key7Destinations: [],
        key8Destinations: [],
        key9Destinations: [],
        keySHARPDestinations: [],
        keySTARDestinations: [],
        meIds: [],
        ivSpeechDetect: false,
        branchId: null,
        announcementsIds: []
      }
    };
  },
  computed: {
    selectedTenant() {
      return this.$store.state.selectedTenant;
    },

    timeoutDestinationsSelected() {
      return this.editForm.timeoutDestinations.length
        ? this.editForm.timeoutDestinations.findIndex(
            ({ id }) => id !== null
          ) !== -1
        : false;
    }
  },

  watch: {
    timeoutDestinationsSelected(val) {
      if (!val) {
        this.editForm.timeout = null;
      }
    },

    editVisible(val) {
      if (val) {
        this.getAnnouncementOptions();
      }
    },

    selectedIvr(value) {
      if(!value) return;

      const {
        name,
        phoneNums,
        digitTimeout,
        allowDisa,
        loopOnTimeout,
        loopOnWrongKeyPress,
        maxLoops,
        timeout,
        meIds,
        timeoutDestinations,
        wrongKeyPressDestinations,
        ivSpeechDetect,
        branchId,
        announcements
      } = value;

      if (ivSpeechDetect) {
        this.selectedMeId = meIds.length ? meIds[0] : null;
      }

      this.editForm.meIds = [...meIds];
      this.editForm.name = name;
      this.editForm.timeout = timeout;
      this.editForm.maxLoops = maxLoops;
      this.editForm.digitTimeout = digitTimeout;
      this.extensionDialing = allowDisa === "on";
      this.editForm.loopOnTimeout = loopOnTimeout === "on";
      this.editForm.loopOnWrongKeyPress = loopOnWrongKeyPress === "on";
      this.editForm.timeoutDestinations = [...timeoutDestinations];
      this.editForm.wrongKeyPressDestinations = [...wrongKeyPressDestinations];
      this.ivSpeechDetect = !!ivSpeechDetect;

      this.keyOptions.forEach(key => {
        const prefix = `key${key}Destinations`;
        this.editForm[prefix] = value[prefix];
      });

      if (phoneNums && phoneNums.length) {
        this.editForm.phoneNums = phoneNums.map(num => num.id);
      }

      if (branchId) {
        this.editForm.branchId = branchId;
      }

      // Set announcements
      if (announcements && announcements.length) {
        this.announcementKeys = [];
        for (let i = 0; i < announcements.length; i++) {
          const announcement = announcements[i];
          this.announcementKeys.push({
            id: announcement.id,
            options: [{
              id: announcement.id,
              name: announcement.name,
              number: announcement.description,
            }],
            value: {
              id: announcement.id,
              name: announcement.name,
              number: announcement.description,
            }
          });
        }
      }
    },

    clearFormFields(value) {
      if (value) {
        this.clearFields();
      }
    }
  },

  methods: {
    changeDraggableKey() {
      const randomString = () => Math.random().toString(36).slice(2);
      this.draggableKey = `${randomString()}-${randomString()}$${randomString()}`;
    },

    removeAnnouncementKey(idx) {
      this.announcementKeys.splice(idx, 1);
    },

    async remoteSearchAnnouncements(query, idx) {
      if (query && query.length) {
        try {
          const res = await fetch(`${apiV2Host}/admin/announcements?tenantId=${this.selectedTenant.id}&name=${query}`, {
            method: "GET",
            headers: {
              Authorization: `Bearer ${this.$store.state.auth.accessToken}`,
              "Content-Type": "application/json",
            },
          });
          if (res.ok) {
            const result = await res.json();
            this.announcementKeys[idx].options = [...result.records];
          }
        } catch (error) {
          this.$notify({
            title: error.name,
            message: error.message,
            type: "error"
          });
        }
      }
    },

    async getAnnouncementOptions() {
      try {
        const { accessToken } = this.$store.state.auth;
        const { id: tenantId } = this.selectedTenant;

        const res = await fetch(`${apiV2Host}/admin/announcements?tenantId=${tenantId}`, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${accessToken}`,
            "Content-Type": "application/json"
          }
        });

        if (!res.ok) return;

        const { records } = await res.json();
        this.announcementOptions = records;

        // Map options to the announcement keys in the form
        this.announcementKeys = this.announcementKeys.map(announcement => ({
          id: announcement.id,
          // Populate with unique options
          options: [...new Map([...announcement.options, ...records].map(item => [item.id, item])).values()],
          value: {
            id: announcement.id,
            name: announcement.name,
            number: announcement.description
          }
        }));

      } catch (error) {
        this.$notify({
          title: error.name,
          message: error.message,
          type: 'error'
        });
      }
    },

    onAnnouncementSelection(announcementId, i) {
      if (!announcementId) {
        this.announcementKeys[i].value = null;
        return;
      }

      const announcement = this.announcementKeys[i].options.find(
        (an) => an.id === announcementId
      );

      this.announcementKeys[i].value = {
        id: announcementId,
        name: announcement.name,
        number: announcement.number,
      };
    },

    addAnnouncementKey() {
      // Add new field and pre-populate with announcement options
      this.announcementKeys.push({
        options: [...this.announcementOptions],
        id: null,
        value: null,
      });
    },

    onNewMediafileCreation(newMediafile) {
      this.newMediafile = newMediafile;
    },

    onTimeoutDestinationsSelection(destinations) {
      this.editForm.timeoutDestinations = [...destinations];
    },

    onWrongKeyPressDestinationsSelection(destinations) {
      this.editForm.wrongKeyPressDestinations = [...destinations];
    },

    onKeyDestinationsSelection(key, destinations) {
      this.editForm[`key${key}Destinations`] = [...destinations];
    },

    getKeyLabel(key) {
      return typeof key === "string" ? (key === "SHARP" ? "#" : "*") : key;
    },

    onMediafileSelected(meIds) {
      this.editForm.meIds = [meIds];
      this.selectedMeId = meIds;
    },

    onMediafilesSelected(meIds) {
      this.editForm.meIds = [...meIds];
    },

    onPhoneNumsChanged() {
      let tempArray = this.selectedIvr.phoneNums.map(t => {
        return t.id;
      });

      if (!isEqual(tempArray, this.editForm.phoneNums)) {
        this.editForm.phoneNumsChanged = true;
      } else {
        this.editForm.phoneNumsChanged = false;
      }
    },

    clearFields() {
      this.newMediafile = null;
      this.selectedMeId = null;
      this.activeMediafileTabName = "existing";
      Object.keys(this.editForm).forEach(key => {
        if (typeof this.editForm[key] === "string") {
          this.editForm[key] = "";
        }
        if (typeof this.editForm[key] === "boolean") {
          this.editForm[key] = false;
        }
        if (typeof this.editForm[key] === "number") {
          this.editForm[key] = null;
        }
        if (Array.isArray(this.editForm[key])) {
          this.editForm[key] = [];
        }
      });
      this.activeTabName = "general";

      this.keyOptions.forEach(key => {
        this.editForm[`key${key}Destinations`] = [];
      });
      this.editForm.timeoutDestinations = [];
      this.editForm.wrongKeyPressDestinations = [];
      this.editForm.announcementsIds = [];
      this.announcementKeys = [];
      this.ivSpeechDetect = false;
      this.extensionDialing = false;
    },

    validateDestinations(destinations, type) {
      if (destinations.length > 1 && destinations.find(dst => !dst.id)) {
        this.$notify({
          title: `Empty ${type} Destination field`,
          message: "Don't leave any destination field to be empty.",
          type: "error"
        });
        return;
      }

      // Self-reference check for IVR destinations
      if (type === "Timeout" && (this.selectedIvr && destinations.some(dst => dst.type === "IVR" && dst.id === this.selectedIvr.id))) {
        this.$notify({
          title: `Invalid ${type} Destination`,
          message: "A virtual receptionist cannot point to itself. Please select a different destination.",
          type: "error"
        });
        return;
      }

      let finalDestinations = [];
      if (destinations.length === 1 && destinations[0].id === null) {
        return finalDestinations;
      }

      if (destinations.length) {
        destinations.forEach((dst, idx) => (dst.order = idx + 1));

        finalDestinations = destinations.map(({ id, type, order }) => ({
          id,
          type,
          order
        }));
      }
      return finalDestinations;
    },

    editOperation() {
      this.editForm.allowDisa = this.extensionDialing ? "on" : "";
      this.editForm.maxLoops = this.editForm.maxLoops || 2;
      this.editForm.timeout = this.editForm.timeout || 10;
      this.editForm.ivSpeechDetect = this.ivSpeechDetect ? 1 : 0;

      this.editForm.announcementsIds = this.announcementKeys
        .filter(item => item.id !== null)
        .map(item => item.id);

      const editBody = { ...this.editForm };

      //speech detect and media file settings
      editBody.meIds = [];

      if(this.ivSpeechDetect){
        editBody.meIds = this.selectedMeId ? [this.selectedMeId] : [];
        if(!editBody.meIds.length){
          editBody.meIds = this.editForm.meIds.length ? [this.editForm.meIds[0]] : [];
        }
      }

      if(!this.ivSpeechDetect){
        editBody.meIds = this.editForm.meIds.map(item => +item);

        if(!editBody.meIds.length){
          editBody.meIds = this.selectedMeId ? [this.selectedMeId] : [];
        }
      }

      editBody.loopOnTimeout = this.editForm.loopOnTimeout ? "on" : ""
      editBody.loopOnWrongKeyPress = this.editForm.loopOnWrongKeyPress
        ? "on"
        : "";

      const wrongKeyPressDestinations = this.validateDestinations(
        editBody.wrongKeyPressDestinations,
        "Wrong Key Press"
      );

      const timeoutDestinations = this.validateDestinations(
        editBody.timeoutDestinations,
        "Timeout"
      );

      if (wrongKeyPressDestinations && timeoutDestinations) {
        editBody.timeoutDestinations = timeoutDestinations;
        editBody.wrongKeyPressDestinations = wrongKeyPressDestinations;
      } else {
        return;
      }

      let validatedDestinations = {};
      for (let i = 0; i < this.keyOptions.length; i++) {
        const key = this.keyOptions[i];
        validatedDestinations[
          `key${key}Destinations`
        ] = this.validateDestinations(
          this.editForm[`key${key}Destinations`],
          `Key ${key}`
        );
      }

      let keyPressDestinationsAreValid = true;
      for (let i = 0; i < this.keyOptions.length; i++) {
        const key = this.keyOptions[i];
        const prefix = `key${key}Destinations`;
        const destinations = validatedDestinations[prefix];
        if (destinations) {
          editBody[prefix] = [...destinations];
        } else {
          keyPressDestinationsAreValid = false;
          break;
        }
      }

      if (keyPressDestinationsAreValid) {
        this.$emit("editOperation", editBody);
      }
    },

    cancelOperation() {
      this.$emit("cancelOperation", "edit");
    },

    remoteMethodForAvailableNums(query) {
      this.$emit("remoteMethodForAvailableNums", query);
    }
  },

  filters: {
    formatNumber(value) {
      return value
        ? `(${value.substr(0, 3)}) ${value.substr(3, 3)}-${value.substr(6, 4)}`
        : value;
    }
  }
};
</script>

<style lang="scss" scoped>
@import "../../../../assets/scss/variables";

.checkbox-group {
  max-width: 600px;
  margin: auto;
}

.el-tabs {
  max-width: 600px;
  margin: auto;
}

.inline-form-group .el-checkbox {
  margin-left: 0;
}

.el-tab-pane .inline-form-group .el-col .el-select {
  margin: 5px auto;
}
</style>
